import { computed, type ComputedRef, isReadonly, isRef, type Ref, ref, watch } from "vue"
import {
  AS_ADMIN,
  type MutateOptions,
  type SchemaField,
  type SchemaLayout,
  type SchemaLike,
} from "@auger/core"
import { cleanSchemaFields, type SavedFilter, validateSchemaField } from "@auger/core/fields"
import { getAuger } from "@/init/auger"
import { useUserStore } from "@/stores/user"
import { errorMessage } from "@/utils/errors"
import type { ComposableDeps } from "./types"

// Schema editor's working shape — every property initialized for form binding
// (empty strings instead of undefined; enum values as a CSV string for the
// input field; tempKey + isNew for row tracking in the editor's field list). See
// `expandFields` / `cleanFields` for the roundtrip with the compact storage
// shape declared by SchemaField.
export interface EditableField {
  key: string
  label: string
  type: string
  description: string
  values: string
  recordType: string
  path: string
  // A rollup field's reduction of what its path reaches: count, first or last.
  aggregate?: string
  isArray: boolean
  isHidden: boolean
  // A link field's filter on the records it can link to; a rollup field's on the
  // rows it reads (kept as stored; the editor doesn't edit it).
  filter: SavedFilter | null
  tempKey: number
  isNew: boolean
}

// Accept any schema-shaped source — Schema (canonical schemas-store row) or
// RecordTypeSchema (looser shape from getType(name)?.schema) both satisfy
// SchemaLike. Typing against the common-subset interface lets useRecord /
// useList / the schema editor all share this composable without casts.
type SchemaSource = SchemaLike | null | undefined
type SchemaInput = SchemaSource | Ref<SchemaSource> | ComputedRef<SchemaSource>

type ListUsingSchema = { id: number; name: string }

/**
 * The write options for a list's own schemas: its listing schema and the list
 * record's schema, which the list grid's "add field" writes. Admins write as
 * heavybit_admin. Staff send no role: Hasura's heavybit_staff rule lets them
 * write a schema that no type and no non-list record uses, so they can add a
 * field to a list but not change a type's or a record's schema.
 */
export function listSchemaWriteOptions(): MutateOptions | undefined {
  return useUserStore().isAdmin ? AS_ADMIN : undefined
}

/**
 * Composable for managing a schema instance — viewing, editing, saving its
 * fields, and tracking how many records / listings use it.
 *
 * `schemaRef` can be a plain object, a ref, or a computed (a parent ref's
 * readonly view). For readonly sources, an internal writable ref is created
 * and kept in sync via a watcher so saves can update it locally.
 *
 * @example
 *   const recordSchemaRef = computed(() => record.value?.schema)
 *   const recordSchema = useSchema(recordSchemaRef, { client, toast })
 */
export function useSchema(schemaRef: SchemaInput, deps: ComposableDeps) {
  const { client, toast } = deps

  // Normalize to ref - if it's readonly (computed), create a writable ref that syncs from it
  const isReadonlyRef = isRef(schemaRef) && isReadonly(schemaRef)
  const schema: Ref<SchemaSource> = isReadonlyRef
    ? ref<SchemaSource>(schemaRef.value)
    : isRef(schemaRef)
      ? (schemaRef as Ref<SchemaSource>)
      : ref<SchemaSource>(schemaRef)

  // If the source is readonly, watch it and sync changes to our internal ref
  if (isReadonlyRef) {
    watch(schemaRef as Ref<SchemaSource>, (newVal) => {
      schema.value = newVal
    })
  }

  // --- State ---
  const loading = ref(false)
  const error = ref<Error | null>(null)
  const recordCount = ref(0)
  const listsUsingAsListing = ref<ListUsingSchema[]>([])

  // Editable fields - initialized from schema, modified by user
  const name = ref("")
  const description = ref("")
  const fields = ref<EditableField[]>([])
  const recordType = ref<string | null>(null)
  const layout = ref<SchemaLayout | null>(null)
  const icon = ref<string | null>(null)
  const color = ref<string | null>(null)

  // --- Computed ---

  const hasSchema = computed(() => !!schema.value?.id)

  const hasChanges = computed(() => {
    if (!hasSchema.value) return fields.value.length > 0 || name.value.trim() !== ""
    const s = schema.value
    if (!s) return false
    if (name.value !== (s.name || "")) return true
    if (description.value !== (s.description || "")) return true
    if (recordType.value !== (s.record_type || null)) return true
    if (icon.value !== (s.icon || null)) return true
    if (color.value !== (s.color || null)) return true
    if (JSON.stringify(layout.value) !== JSON.stringify(s.layout || null)) return true
    return !fieldsEqual(fields.value, s.fields || [])
  })

  // --- Initialization ---

  // Initialize editable state from current schema (called automatically via watcher)
  function initializeFromSchema() {
    const s = schema.value
    if (s) {
      name.value = s.name || ""
      description.value = s.description || ""
      fields.value = expandFields(s.fields || [])
      recordType.value = s.record_type || null
      layout.value = s.layout || null
      icon.value = s.icon || null
      color.value = s.color || null
    } else {
      name.value = ""
      description.value = ""
      fields.value = []
      recordType.value = null
      layout.value = null
      icon.value = null
      color.value = null
    }
  }

  // Auto-initialize when schema changes
  watch(
    schema,
    () => {
      initializeFromSchema()
    },
    { immediate: true }
  )

  // --- Data Fetching ---

  // Fetch usage counts (records using this schema + lists using it as a
  // listing schema).
  function fetchUsage(): Promise<{ records: number; lists: ListUsingSchema[] }> {
    const id = schema.value?.id
    if (!id) {
      recordCount.value = 0
      listsUsingAsListing.value = []
      return Promise.resolve({ records: 0, lists: [] })
    }

    return getAuger(client)
      .schemas.usage(id)
      .then((usage) => {
        recordCount.value = usage.recordCount
        listsUsingAsListing.value = [...usage.lists]
        return { records: recordCount.value, lists: listsUsingAsListing.value }
      })
  }

  // --- Schema Mutations ---

  // Save changes (update existing or create new). Returns the saved schema.
  // `opts` is the write's role: the admin editors pass AS_ADMIN; a list's own
  // schemas pass listSchemaWriteOptions(). With no role the write runs as
  // heavybit_staff, whose rule allows only a list's schemas.
  function save(opts?: MutateOptions) {
    if (!name.value.trim()) {
      toast?.error?.("Schema name is required")
      return Promise.reject(new Error("Schema name is required"))
    }

    const cleanedFields = cleanFields(fields.value)
    loading.value = true

    const id = schema.value?.id
    if (id) {
      const _set = {
        name: name.value.trim(),
        description: description.value.trim() || null,
        fields: cleanedFields,
        layout: layout.value || null,
        record_type: recordType.value || null,
        icon: icon.value || null,
        color: color.value || null,
      }

      return getAuger(client)
        .schemas.update(id, _set, opts)
        .then((updated) => {
          schema.value = updated
          toast?.success?.("Schema updated")
          return updated
        })
        .catch((err) => {
          const error = errorMessage(err, { name: name.value })
          toast?.error?.(`Failed to update schema: ${error}`)
          throw err
        })
        .finally(() => {
          loading.value = false
        })
    } else {
      return getAuger(client)
        .schemas.create(
          {
            name: name.value.trim(),
            description: description.value.trim() || null,
            fields: cleanedFields,
            layout: layout.value || null,
            record_type: recordType.value || null,
            icon: icon.value || null,
            color: color.value || null,
          },
          opts
        )
        .then((created) => {
          schema.value = created
          toast?.success?.("Schema created")
          return created
        })
        .catch((err) => {
          const error = errorMessage(err)
          toast?.error?.(`Failed to create schema: ${error}`)
          throw err
        })
        .finally(() => {
          loading.value = false
        })
    }
  }

  // The schema_id column write goes through the domain's record update.
  function setRecordSchema(recordId: number, schemaId: number | null) {
    return getAuger(client)
      .records.update(recordId, { schema_id: schemaId }, { read: false })
      .then(() => ({
        id: recordId,
        schema_id: schemaId,
      }))
  }

  // Link this schema to a record by setting record.schema_id.
  function linkToRecord(recordId: number) {
    const id = schema.value?.id
    if (!id) {
      return Promise.reject(new Error("No schema to link"))
    }

    loading.value = true

    return setRecordSchema(recordId, id)
      .then((updated) => {
        toast?.success?.("Schema linked")
        return updated
      })
      .catch((err) => {
        const error = errorMessage(err)
        toast?.error?.(`Failed to link schema: ${error}`)
        throw err
      })
      .finally(() => {
        loading.value = false
      })
  }

  // Unlink schema from a record (set schema_id to null).
  function unlinkFromRecord(recordId: number) {
    loading.value = true

    return setRecordSchema(recordId, null)
      .then((updated) => {
        toast?.success?.("Schema unlinked")
        return updated
      })
      .catch((err) => {
        const error = errorMessage(err)
        toast?.error?.(`Failed to unlink schema: ${error}`)
        throw err
      })
      .finally(() => {
        loading.value = false
      })
  }

  return {
    // Schema ref
    schema,

    // Editable state
    name,
    description,
    fields,
    layout,
    recordType,
    icon,
    color,

    // Computed
    hasSchema,
    hasChanges,

    // Status
    loading,
    error,
    recordCount,
    listsUsingAsListing,

    // Methods
    reset: initializeFromSchema,
    fetch,
    fetchUsage,
    save,
    linkToRecord,
    unlinkFromRecord,
  }
}

// Field Utilities ===============================================================

// Strip editor-only properties (tempKey, isNew, empty fields) and shape values
// for storage — a thin wrapper over core's `cleanSchemaFields`.
export function cleanFields(fields: EditableField[] | SchemaField[]): SchemaField[] {
  return cleanSchemaFields(fields)
}

// Expand from compact storage shape to editor shape — every property
// initialized for form binding (empty strings, false booleans, enum values
// as CSV). Adds tempKey + isNew so the editor can track rows. Properties
// the editor does not show ride along, so a save stores them back unchanged.
export function expandFields(fields: SchemaField[] | null | undefined): EditableField[] {
  if (!Array.isArray(fields)) return []

  let tempKey = 1
  return fields.map((field) => ({
    ...field,
    key: field.key || "",
    label: field.label || "",
    type: field.type || "string",
    description: (field.description as string | undefined) || "",
    values: field.values ? (field.values as string[]).join(", ") : "",
    recordType: field.recordType || "",
    path: (field as { path?: string }).path || "",
    aggregate: (field as { aggregate?: string }).aggregate || "",
    isArray: !!field.isArray,
    isHidden: !!field.isHidden,
    filter: field.filter ?? null,
    tempKey: tempKey++,
    isNew: false,
  }))
}

// Why a field in the editor can't be saved: core's validation of the field as
// it will be stored, plus a key another field in the list already has.
export function fieldErrors(field: EditableField, fields: EditableField[]): string[] {
  const values = field.values
    .split(",")
    .map((v) => v.trim())
    .filter(Boolean)
  const { errors } = validateSchemaField({ ...field, values, recordType: field.recordType || null })
  const duplicate = fields.some((f) => f !== field && !!f.key && f.key === field.key)
  return duplicate ? [...errors, "Another field has this key"] : errors
}

// Check if two field arrays are equivalent (for change detection).
export function fieldsEqual(
  a: EditableField[] | SchemaField[],
  b: EditableField[] | SchemaField[]
): boolean {
  const cleanA = JSON.stringify(cleanFields(a))
  const cleanB = JSON.stringify(cleanFields(b))
  return cleanA === cleanB
}
